import { Skeleton } from "@dropbox/dig-components/skeleton";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { EmbeddedUpsells } from "@mirage/growth/onboarding/embedded-upsells/EmbeddedUpsells";
import { ExtensionUpsellBannerContainer } from "@mirage/growth/onboarding/extension-upsell/ExtensionUpsellBannerContainer";
import { ConversationInput } from "@mirage/mosaics/Chat/components/chat/ConversationInput";
import { ConversationMessages } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ConversationMessages";
import type { MarkdownResponseProps } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownResponse";
import { ConversationSuggestedQueries } from "@mirage/mosaics/Chat/components/chat/suggestions/ConversationSuggestedQueries";
import { AddSourcesModal } from "@mirage/mosaics/Chat/components/compose-sources/AddSourcesModal";
import { WorkflowAgentMessageHeader } from "@mirage/mosaics/ComposeAssistant/components/workflow-agent/WorkflowAgentMessageHeader";
import { useManuallyAddedSources } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useManuallyAddedSources";
import { useResultsShoppingCart } from "@mirage/mosaics/ResultsShoppingCart/hooks/useResultsShoppingCart";
import {
  cancelConversationRequest,
  postUserMessage,
} from "@mirage/service-conversation";
import useConversation from "@mirage/service-conversation/hooks/useConversation";
import type { ConversationType } from "@mirage/service-conversation/router_state_types";
import {
  type ConversationMessage,
  type DocumentInfo,
  type ModelId,
  type NewUserMessage,
  type UserMessageOptions,
  type WorkflowAgent,
} from "@mirage/service-conversation/types";
import { convertToChatMessage } from "@mirage/service-conversation/utils/convertToChatMessage";
import { getDashActionSurfaceComponentLoggingSource } from "@mirage/service-conversation/utils/loggingUtils";
import type { ComposeParams } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/assist_compose_pb";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n from "@mirage/translations";
import cx from "classnames";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import styles from "./ConversationChat.module.css";

interface ConversationChatProps {
  conversationId: string;
  handleClickAdd: () => void;
  handleCloseAddSourcesModal: () => void;
  isAddSourceModalOpen: boolean;
  agent?: WorkflowAgent;
  variant?: "default" | "condensed";
  selectedModel?: ModelId;
  setSelectedModel: (model: ModelId) => void;
  startingMessageNode?: ReactNode;
  showEmptyChatInstructions?: boolean;
  messages?: ConversationMessage[];
  progressComponent?: React.ReactNode;
  isWaitingForResponse: boolean;
  agentPayload: ComposeParams;
  getConversationAttachments: () => Promise<DocumentInfo[]>;
  userMessageOptions?: UserMessageOptions;
  setUserMessageOptions: (options: UserMessageOptions) => void;
  allowCustomSources?: boolean;
  shouldHideSources?: boolean;
  showModelPickerButton: boolean;
  showContextPickerButton: boolean;
  showIncognitoButton: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
  setIsSubmittingMessage: Dispatch<SetStateAction<boolean>>;
  overridePostUserMessage?: (message: NewUserMessage) => void;
  overrideMarkdownRenderer?: (props: MarkdownResponseProps) => React.ReactNode;
  conversationType?: ConversationType;
  showDraftGeneratedButton?: boolean;
  onToggleMobileView?: () => void;
  showFollowUpSuggestionsSkeleton?: boolean;
}

export const ConversationChat = memo(
  ({
    variant = "default",
    conversationId,
    handleClickAdd,
    handleCloseAddSourcesModal,
    isAddSourceModalOpen,
    agent,
    startingMessageNode,
    showEmptyChatInstructions = false,
    agentPayload,
    messages = [],
    progressComponent,
    isWaitingForResponse,
    getConversationAttachments,
    userMessageOptions,
    setUserMessageOptions,
    logComposeEvent,
    allowCustomSources,
    shouldHideSources = false,
    showModelPickerButton = true,
    showContextPickerButton = true,
    showIncognitoButton = true,
    selectedModel,
    setSelectedModel,
    setIsSubmittingMessage,
    overridePostUserMessage,
    overrideMarkdownRenderer,
    conversationType,
    showDraftGeneratedButton,
    onToggleMobileView,
    showFollowUpSuggestionsSkeleton,
  }: ConversationChatProps) => {
    const conversation = useConversation(conversationId);
    const [conversationInputHeight, setConversationInputHeight] = useState<
      number | undefined
    >(undefined);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const {
      manuallyAddedSources,
      setManuallyAddedSources,
      addSource,
      removeSource,
    } = useManuallyAddedSources();
    const isContextPickerEnabled = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_05_27_assist_context_picker"),
    );
    const isIncognitoModeEnabled = convertFeatureValueToBool(
      useFeatureFlagValue("dash_2025_06_13_assist_incognito_mode"),
    );
    const hideExtensionUpsell = useMemo(() => {
      // Most of upsell logic is in the extension upsell banner component/hook
      // We just want to hide banner if user is in writing mode
      return !!conversation?.markdownDraft;
    }, [conversation]);

    const PLACEHOLDER_TEXTS = useMemo(() => {
      const baseText = [i18n.t("conversation_prompt_placeholder")];

      // Add conditional placeholder text here based on feature flags or other conditions
      // Example: if (someFeatureEnabled && messages.length === 0) { return [...additionalPrompts, ...baseText]; }

      return baseText;
    }, [messages.length]);

    const { isActivated, cartItems, setIsActivated } = useResultsShoppingCart();
    const startingMessageNodeToDisplay = useMemo(() => {
      if (agent) {
        return <WorkflowAgentMessageHeader agent={agent} />;
      }
      return startingMessageNode;
    }, [agent, startingMessageNode]);

    const composeMessages = useMemo(
      () => messages?.map(convertToChatMessage) || [],
      [messages],
    );

    const handleSubmit = useCallback(
      async (text: string) => {
        setIsSubmittingMessage(true);

        const attachments = await getConversationAttachments();

        // Cancel any ongoing request before sending a new message
        // This is handled automatically in the service layer
        cancelConversationRequest(conversationId);

        (overridePostUserMessage || postUserMessage)({
          conversationId,
          message: text,
          model: selectedModel,
          agentPayload: JSON.stringify(agentPayload),
          attachments,
          agentIds: userMessageOptions?.agentIds || [],
          source:
            getDashActionSurfaceComponentLoggingSource("compose_chat_pane"),
          searchParams: userMessageOptions?.searchParams,
          skipPersistence: userMessageOptions?.skipPersistence,
          clientContext: userMessageOptions?.clientContext,
        });

        setUserMessageOptions({
          ...userMessageOptions,
          // Always reset the client context after posting it with the message
          clientContext: undefined,
          // Unless it's a workflow agent (with persistAgentIds set to true), reset the agentIds
          agentIds: !userMessageOptions?.persistAgentIds
            ? []
            : userMessageOptions?.agentIds,
        });

        // Reset any manually added sources after each message submission
        setManuallyAddedSources([]);
      },
      [
        conversationId,
        selectedModel,
        agentPayload,
        setManuallyAddedSources,
        setIsSubmittingMessage,
        setUserMessageOptions,
        getConversationAttachments,
        userMessageOptions,
        overridePostUserMessage,
      ],
    );

    const handleClickSuggestedQuery = useCallback(
      (query: string) => {
        handleSubmit(query);
      },
      [handleSubmit],
    );

    useEffect(() => {
      if (messages.length > 0 || PLACEHOLDER_TEXTS.length <= 1) {
        setPlaceholderIndex(0);
        return;
      }

      const interval = setInterval(() => {
        setPlaceholderIndex(
          (prevIndex) => (prevIndex + 1) % PLACEHOLDER_TEXTS.length,
        );
      }, 5000); // Change every 5 seconds

      return () => clearInterval(interval);
    }, [messages.length, PLACEHOLDER_TEXTS.length]);

    const displayMessages = composeMessages;

    useEffect(() => {
      if (isActivated) {
        setIsActivated(false);
        setManuallyAddedSources((prevSources) => {
          const existingUUIDs = new Set(prevSources.map(getSourceUUID));
          const newSources = [...cartItems].filter((source) => {
            const uuid = getSourceUUID(source);
            return uuid && !existingUUIDs.has(uuid);
          });
          return [...prevSources, ...newSources];
        });
      }
    }, [isActivated, setIsActivated, cartItems, setManuallyAddedSources]);

    return (
      <div
        className={cx(styles.container, {
          [styles.embedContainer]: isEmbedPage(),
          [styles.condensed]: variant === "condensed",
        })}
      >
        <div className={styles.content}>
          <ConversationMessages
            conversationId={conversation?.serverConversationId}
            variant={variant}
            messages={displayMessages}
            startingMessageNode={startingMessageNodeToDisplay}
            isWaitingForResponse={isWaitingForResponse}
            progressComponent={progressComponent}
            onRemoveSource={removeSource}
            isLastMessageFollowUpSuggestions={false} // do i need this?
            artifacts={[]}
            logComposeEvent={logComposeEvent}
            onOpenArtifact={onToggleMobileView || (() => {})}
            userMessageOptions={userMessageOptions}
            footer={
              showFollowUpSuggestionsSkeleton ? (
                <div className={styles.suggestedQueriesSkeletonContainer}>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton.Box
                      key={index}
                      height={28}
                      withBorderRadius="xlarge"
                      className={styles.suggestedQueriesSkeleton}
                    />
                  ))}
                </div>
              ) : (
                conversation?.suggestedQueries &&
                conversation?.suggestedQueries.length > 0 &&
                !isWaitingForResponse &&
                !userMessageOptions?.hideFollowUpSuggestions && (
                  <ConversationSuggestedQueries
                    suggestedQueries={conversation.suggestedQueries}
                    onSubmitText={handleClickSuggestedQuery}
                    logComposeEvent={logComposeEvent}
                    conversationId={conversation?.serverConversationId}
                    assistRequestId={conversation?.assistRequestId}
                  />
                )
              )
            }
            fullHeight={showEmptyChatInstructions}
            hideSources={shouldHideSources || !allowCustomSources}
            assistRequestId={conversation?.assistRequestId}
            spanId={conversation?.spanId}
            conversationInputHeight={conversationInputHeight}
            overrideMarkdownRenderer={overrideMarkdownRenderer}
            conversationType={conversationType}
            showDraftGeneratedButton={showDraftGeneratedButton}
          />
          {!isEmbedPage() && !hideExtensionUpsell && (
            <ExtensionUpsellBannerContainer />
          )}
          {isEmbedPage() && <EmbeddedUpsells />}
          <ConversationInput
            variant={variant}
            sources={manuallyAddedSources}
            onSubmit={handleSubmit}
            logComposeEvent={logComposeEvent}
            messages={composeMessages}
            placeholderOverride={PLACEHOLDER_TEXTS[placeholderIndex]}
            handleClickAdd={handleClickAdd}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            showAddSourcesButton={allowCustomSources}
            conversationId={conversation?.serverConversationId}
            assistRequestId={conversation?.assistRequestId}
            showModelPickerButton={showModelPickerButton}
            showContextPickerButton={
              isContextPickerEnabled && showContextPickerButton
            }
            showIncognitoButton={isIncognitoModeEnabled && showIncognitoButton}
            userMessageOptions={userMessageOptions}
            setUserMessageOptions={setUserMessageOptions}
            buttonSize={variant === "condensed" ? "medium" : "large"}
            isWorkflowAgentPreview={agent?.id === "preview"}
            onRemoveSource={removeSource}
            onHeightChange={setConversationInputHeight}
          />
          {isAddSourceModalOpen && (
            <AddSourcesModal
              sources={manuallyAddedSources}
              addSource={addSource}
              removeSource={removeSource}
              onRequestClose={handleCloseAddSourcesModal}
              logComposeEvent={logComposeEvent}
            />
          )}
        </div>
      </div>
    );
  },
);

ConversationChat.displayName = "ConversationChat";
